import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type {
  ContractDetail,
  TemplateDetail,
  TemplateSummary,
} from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { SchemaForm } from '../components/SchemaForm';

type Step = 'pick' | 'fill' | 'preview';

export function CreateContractPage() {
  const { fetcher } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState<Step>('pick');
  const [templateId, setTemplateId] = useState('');
  const [mergeData, setMergeData] = useState<Record<string, string>>({});
  const [previewHtml, setPreviewHtml] = useState('');

  const tplListQ = useQuery({
    queryKey: ['templates'],
    queryFn: () => fetcher<{ templates: TemplateSummary[] }>('/api/templates'),
  });
  const tplQ = useQuery({
    queryKey: ['template', templateId],
    queryFn: () => fetcher<TemplateDetail>(`/api/templates/${templateId}`),
    enabled: !!templateId && step !== 'pick',
  });

  // Reset merge_data with defaults when template loads.
  useEffect(() => {
    if (tplQ.data) {
      const initial: Record<string, string> = {};
      for (const v of tplQ.data.variable_schema ?? []) {
        if (v.default !== undefined) initial[v.name] = v.default;
      }
      setMergeData(initial);
    }
  }, [tplQ.data]);

  const previewM = useMutation({
    mutationFn: () =>
      fetcher<{ html: string }>(`/api/templates/${templateId}/preview`, {
        method: 'POST',
        body: JSON.stringify({ merge_data: mergeData }),
      }),
    onSuccess: (res) => {
      setPreviewHtml(res.html);
      setStep('preview');
    },
  });

  const createM = useMutation({
    mutationFn: () =>
      fetcher<ContractDetail>('/api/contracts', {
        method: 'POST',
        body: JSON.stringify({ template_id: templateId, merge_data: mergeData }),
      }),
    onSuccess: (c) => nav(`/contracts/${c.id}`),
  });

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">New contract</h1>
      <ol className="flex gap-2 text-sm text-slate-600">
        <li className={step === 'pick' ? 'font-semibold text-brand' : ''}>
          1. Template
        </li>
        <li>›</li>
        <li className={step === 'fill' ? 'font-semibold text-brand' : ''}>
          2. Fill
        </li>
        <li>›</li>
        <li className={step === 'preview' ? 'font-semibold text-brand' : ''}>
          3. Preview
        </li>
      </ol>
      {step === 'pick' ? (
        <div className="card space-y-3">
          <label className="label">Template</label>
          <select
            className="input"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
          >
            <option value="">Choose a template…</option>
            {(tplListQ.data?.templates ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn-primary"
            disabled={!templateId}
            onClick={() => setStep('fill')}
          >
            Next
          </button>
        </div>
      ) : null}
      {step === 'fill' && tplQ.data ? (
        <div className="card space-y-3">
          <SchemaForm
            schema={tplQ.data.variable_schema ?? []}
            value={mergeData}
            onChange={setMergeData}
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn"
              onClick={() => setStep('pick')}
            >
              Back
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={previewM.isPending}
              onClick={() => previewM.mutate()}
            >
              Preview
            </button>
          </div>
          {previewM.isError ? (
            <p className="text-sm text-red-600">
              {(previewM.error as Error).message}
            </p>
          ) : null}
        </div>
      ) : null}
      {step === 'preview' ? (
        <div className="card space-y-3">
          <iframe
            title="contract preview"
            sandbox=""
            srcDoc={previewHtml}
            className="h-96 w-full rounded border border-slate-200"
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn"
              onClick={() => setStep('fill')}
            >
              Back
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={createM.isPending}
              onClick={() => createM.mutate()}
            >
              Save draft
            </button>
          </div>
          {createM.isError ? (
            <p className="text-sm text-red-600">
              {(createM.error as Error).message}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
